/**
 * OAuth callback detection utilities
 * Prevents demo mode and auth redirects during OAuth processing
 */
import { resolveInternalNavigationPath } from './internalNavigation';

/**
 * Check if current URL is an OAuth callback
 * Supabase puts tokens in URL hash, Google OAuth uses code param
 */
export function isOAuthCallbackUrl(): boolean {
  const hash = window.location.hash;
  const search = window.location.search;
  
  // Supabase OAuth callback indicators
  const hasAccessToken = hash.includes('access_token');
  const hasRefreshToken = hash.includes('refresh_token');
  
  // Google OAuth code flow
  const hasCode = search.includes('code=');
  
  // Error callback (still an OAuth callback, just failed)
  const hasError = hash.includes('error=') || search.includes('error=');
  
  return hasAccessToken || hasRefreshToken || hasCode || hasError;
}

/**
 * Extract OAuth error details from the URL (search params or hash)
 * Returns null if no error is present.
 */
export interface OAuthError {
  error: string;
  errorCode: string | null;
  errorDescription: string | null;
}

export function getOAuthError(): OAuthError | null {
  const search = new URLSearchParams(window.location.search);
  const hash = new URLSearchParams(window.location.hash.replace('#', '?'));

  const error = search.get('error') || hash.get('error');
  if (!error) return null;

  return {
    error,
    errorCode: search.get('error_code') || hash.get('error_code'),
    errorDescription: decodeURIComponent(
      search.get('error_description') || hash.get('error_description') || ''
    ) || null,
  };
}

/**
 * Map an OAuth callback error onto the message the /auth page shows.
 *
 * A brand-new Google/Microsoft identity is refused at account creation by the
 * invite-only signup gate (enforce_invite_only_signup on auth.users). GoTrue
 * reports any blocked insert as "Database error saving new user", so that
 * string is the signal that someone without an account tried the OAuth
 * buttons and should be pointed at the pricing page instead.
 */
export const INVITE_ONLY_SIGNUP_MESSAGE =
  'That account is not a Nynch member yet. Nynch is invite-only right now: choose a plan at nynch.com/pricing, or sign in with the email address your Nynch account already uses.';

/**
 * Company sign-in (SAML SSO) failures. Our access-token and before-user-created
 * hooks refuse an SSO session whose email is not on a verified domain of the
 * company's connection (or a member made to use SSO), and Supabase reports any
 * hook refusal after a SAML assertion generically, without our reason. Name
 * the likely causes without claiming which one happened.
 */
export const SSO_SIGN_IN_FAILED_MESSAGE =
  'Company sign-in (SSO) did not work. Check that you chose your work account and that its email is on a domain your company connected to Nynch. If it still fails, ask your Nynch admin to check the SSO email domains.';

const SSO_ATTEMPT_KEY = 'nynch:sso-sign-in-attempt';
const SSO_ATTEMPT_MAX_AGE_MS = 15 * 60 * 1000;

/** Remember that the person started a company sign-in, so a failed return is
 * explained as one whatever wording Supabase uses. */
export function markSsoSignInAttempt(): void {
  try { sessionStorage.setItem(SSO_ATTEMPT_KEY, String(Date.now())); } catch { /* storage can be unavailable */ }
}

/** Forget the company sign-in marker (another sign-in method was chosen). */
export function clearSsoSignInAttempt(): void {
  try { sessionStorage.removeItem(SSO_ATTEMPT_KEY); } catch { /* storage can be unavailable */ }
}

/** Read and forget the company sign-in marker. */
export function takeSsoSignInAttempt(): boolean {
  try {
    const started = Number(sessionStorage.getItem(SSO_ATTEMPT_KEY));
    sessionStorage.removeItem(SSO_ATTEMPT_KEY);
    return Number.isFinite(started) && started > 0 && Date.now() - started < SSO_ATTEMPT_MAX_AGE_MS;
  } catch {
    return false;
  }
}

function isSsoFailure(desc: string | null): boolean {
  return !!desc && /\bsaml\b|\bsso\b|single sign-on|company sign-in/i.test(desc);
}

export function oauthErrorMessage(oauthError: OAuthError, ssoAttempt = false): string {
  const desc = oauthError.errorDescription;
  if (ssoAttempt || isSsoFailure(desc)) {
    return SSO_SIGN_IN_FAILED_MESSAGE;
  }
  if (desc?.includes('Database error saving new user')) {
    return INVITE_ONLY_SIGNUP_MESSAGE;
  }
  if (desc?.includes('exchange external code')) {
    return 'Google sign-in failed. This is usually a temporary issue, so please try again. If it persists, try email/password instead.';
  }
  return `Sign-in failed: ${desc || oauthError.error}`;
}

/**
 * Returns true when the URL is an OAuth callback that contains error parameters
 * (as opposed to a successful callback with tokens).
 */
export function isOAuthErrorCallback(): boolean {
  const hash = window.location.hash;
  const search = window.location.search;
  return (hash.includes('error=') || search.includes('error=')) &&
    !hash.includes('access_token');
}

/**
 * Clear OAuth parameters from URL after processing
 */
export function clearOAuthParams(): void {
  if (isOAuthCallbackUrl()) {
    // Remove provider codes/errors/tokens, but keep the validated destination
    // through retries, password recovery and a refresh after callback cleanup.
    const returnTo = resolveInternalNavigationPath(new URLSearchParams(window.location.search).get('returnTo'));
    const params = new URLSearchParams();
    if (returnTo) params.set('returnTo', returnTo);
    const cleanUrl = `${window.location.pathname}${params.size ? `?${params.toString()}` : ''}`;
    window.history.replaceState(window.history.state, document.title, cleanUrl);
  }
}
